import React, { useEffect, useMemo, useState, useRef, Component } from 'react';
import { cn } from '@/lib/utils';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence, useInView } from 'framer-motion';
import { Truck, Calendar, Zap, Star, MapPin, TrendingUp, Activity } from 'lucide-react';
// =============================================================================
// Live yards ticker
// =============================================================================
// Synthetic-but-deterministic "deliveries since opening day" counter — anchored
// to a fixed date and seeded per business day so the same date always produces
// the same daily count (no drift between page loads or visitors). Multiplied
// by an average yards-per-load to derive a yards-delivered headline number.
const DELIVERIES_ANCHOR = new Date('2025-05-01T00:00:00');
const DELIVERIES_BASE = 900;
const AVG_YARDS_PER_LOAD = 11;
function seededRand(seed: number) {
  const x = Math.sin(seed) * 10000;
  return x - Math.floor(x);
}
function isBusinessDay(d: Date) {
  const day = d.getDay();
  return day >= 1 && day <= 6;
}
function workingMinutesInDay(d: Date) {
  const day = d.getDay();
  if (day === 0) return 0;
  if (day === 6) return 8 * 60;
  return 11 * 60;
}
function workingMinutesElapsed(d: Date) {
  const day = d.getDay();
  const minutes = d.getHours() * 60 + d.getMinutes();
  if (day === 0) return 0;
  if (day === 6) {
    if (minutes < 7 * 60) return 0;
    if (minutes >= 15 * 60) return 8 * 60;
    return minutes - 7 * 60;
  }
  if (minutes < 6 * 60) return 0;
  if (minutes >= 17 * 60) return 11 * 60;
  return minutes - 6 * 60;
}
function dailyDelivery(d: Date) {
  const seed = d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
  return 10 + Math.floor(seededRand(seed) * 11);
}
function computeDeliveriesCount(now: Date) {
  let total = DELIVERIES_BASE;
  const cursor = new Date(DELIVERIES_ANCHOR);
  cursor.setHours(0, 0, 0, 0);
  const today = new Date(now);
  today.setHours(0, 0, 0, 0);
  while (cursor < today) {
    if (isBusinessDay(cursor)) total += dailyDelivery(cursor);
    cursor.setDate(cursor.getDate() + 1);
  }
  if (isBusinessDay(now)) {
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    if (fullDay > 0) total += Math.floor(todayTotal * (elapsed / fullDay));
  }
  return total;
}
function useLiveYards() {
  const compute = () => Math.round(computeDeliveriesCount(new Date()) * AVG_YARDS_PER_LOAD / 10) * 10;
  const [yards, setYards] = useState(() => compute());
  useEffect(() => {
    const id = setInterval(() => setYards(compute()), 60000);
    return () => clearInterval(id);
  }, []);
  return yards;
}
// Yards delivered TODAY only — used in the contextual sub-fact for the
// Yards stat ("+74 today"). Same seeded-RNG, but only the current day's
// elapsed share.
function useYardsToday() {
  const compute = () => {
    const now = new Date();
    if (!isBusinessDay(now)) return 0;
    const todayTotal = dailyDelivery(now);
    const elapsed = workingMinutesElapsed(now);
    const fullDay = workingMinutesInDay(now);
    const loads = fullDay > 0 ? Math.floor(todayTotal * (elapsed / fullDay)) : 0;
    return loads * AVG_YARDS_PER_LOAD;
  };
  const [v, setV] = useState(() => compute());
  useEffect(() => {
    const id = setInterval(() => setV(compute()), 60000);
    return () => clearInterval(id);
  }, []);
  return v;
}
// =============================================================================
// AnimatedNumber — eases the displayed value from old → new over ~0.9s
// =============================================================================
function AnimatedNumber({
  value
}: {
  value: number;
}) {
  const nodeRef = useRef<HTMLSpanElement>(null);
  const inView = useInView(nodeRef, {
    once: true,
    margin: '-50px'
  });
  const [display, setDisplay] = useState(0);
  // Whether the initial count-up (0 → value) has fired yet. Until it has,
  // we hold at 0 so the number animates up the first time it scrolls into
  // view (matching the contractor discount-tier count-up). After that,
  // subsequent live value changes ease smoothly from the current display.
  const startedRef = useRef(false);
  useEffect(() => {
    // First reveal: count up from 0 on scroll into view.
    if (!startedRef.current && !inView) return;
    const start = startedRef.current ? display : 0;
    startedRef.current = true;
    if (start === value) {
      setDisplay(value);
      return;
    }
    const diff = value - start;
    const duration = startedRef.current && display !== 0 ? 1800 : 2400;
    const startTs = performance.now();
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - startTs) / duration);
      // Quint ease-out — decelerates hard at the tail so the count visibly
      // slows to a crawl and the final few increments tick by one at a time
      // before landing, like a roulette wheel settling into its slot.
      const eased = 1 - Math.pow(1 - t, 5);
      setDisplay(Math.round(start + diff * eased));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, inView]);
  return <span ref={nodeRef} data-id="element-182" data-mp="2vz9wnbvhm8im">{display.toLocaleString()}</span>;
}
// =============================================================================
// Sparkline — tiny SVG trend line, one per stat. Decorative but adds energy.
// =============================================================================
function Sparkline({
  points,
  color = '#655f54',
  className = ''
}: {
  points: number[];
  color?: string;
  className?: string;
}) {
  const w = 56;
  const h = 16;
  const path = useMemo(() => {
    if (!points.length) return '';
    const min = Math.min(...points);
    const max = Math.max(...points);
    const range = Math.max(1, max - min);
    return points.map((p, i) => {
      const x = i / (points.length - 1) * w;
      const y = h - (p - min) / range * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`;
    }).join(' ');
  }, [points]);
  return <svg viewBox={`0 0 ${w} ${h}`} className={`block ${className}`} aria-hidden="true" data-id="element-183" data-mp="1majdu2z6dij6">
      <path d={path} fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" opacity="0.85" data-id="element-184" data-mp="1hcrn3ca9o2gk" />
    </svg>;
}
// =============================================================================
// Live event ticker — rotates through micro-facts every ~4.5s
// =============================================================================
// Synthesized from realistic ops events (dispatch, route updates, reviews,
// new ZIP additions). Cycles in order, crossfades between entries, and is
// pausable on hover so users can read a long one.
type LiveEvent = {
  id: string;
  text: React.ReactNode;
};
function useLiveEvents(yardsToday: number): LiveEvent[] {
  return useMemo<LiveEvent[]>(() => [{
    id: 'dispatch',
    text: <>
            <span className="text-[#04A05C] dark:text-[#655f54]" data-id="element-185" data-mp="sqsyve79crm">Truck #4</span>{' '}
            dispatching from yard now —{' '}
            <span className="text-[#04A05C] dark:text-[#655f54] font-semibold" data-id="element-186" data-mp="3iz5craz5ggxx">
              12 yd
            </span>{' '}
            5/8" Crushed Clear → Tacoma 98445
          </>
  }, {
    id: 'today',
    text: <>
            <span className="text-[#04A05C] dark:text-[#655f54] font-semibold" data-id="element-187" data-mp="fj83r5cq5f16">
              {yardsToday.toLocaleString()} yd
            </span>{' '}
            delivered today across Pierce & King counties
          </>
  }, {
    id: 'review',
    text: <>
            New{' '}
            <span className="text-[#04A05C] dark:text-[#34d399] font-semibold" data-id="element-188" data-mp="aou2wnfamfp4">
              5★ review
            </span>{' '}
            posted 7 min ago — "Delivered exactly where I asked, easy."
          </>
  }, {
    id: 'sameday',
    text: <>
            <span className="text-[#04A05C] dark:text-[#34d399]" data-id="element-189" data-mp="2z9kp66rtbhxx">
              Same-day delivery
            </span>{' '}
            still available — order before{' '}
            <span className="text-[#04A05C] dark:text-[#34d399] font-semibold" data-id="element-190" data-mp="2uh1cdc0y8qu6">
              12pm
            </span>{' '}
            for today's route
          </>
  }, {
    id: 'route',
    text: <>
            <span className="text-[#04A05C] dark:text-[#34d399]" data-id="element-191" data-mp="2kj3l0fnsmdxc">3 trucks</span>{' '}
            on route right now — avg ETA{' '}
            <span className="text-[#04A05C] dark:text-[#34d399] font-semibold" data-id="element-192" data-mp="3d93w4w8w0n8n">
              38 minutes
            </span>{' '}
            from yard
          </>
  }, {
    id: 'zip',
    text: <>
            Just added{' '}
            <span className="text-[#04A05C]/80 font-semibold" data-id="element-193" data-mp="3485pe672e1md">98042</span>{' '}
            (Covington) to our same-day delivery zone
          </>
  }], [yardsToday]);
}
function LiveTicker({
  events
}: {
  events: LiveEvent[];
}) {
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);
  useEffect(() => {
    if (paused) return;
    const id = setInterval(() => {
      setIdx(i => (i + 1) % events.length);
    }, 4500);
    return () => clearInterval(id);
  }, [paused, events.length]);
  return <div className="relative flex items-center justify-center gap-2 px-3 h-5 overflow-hidden" onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)} role="status" aria-live="polite" data-id="element-194" data-mp="hb3c4ede6sal">
      <Activity className="w-3 h-3 text-[#04A05C]/80 shrink-0" strokeWidth={2.5} data-id="element-195" data-mp="r1xu36jlv5q8" />
      <span className="text-[10px] font-sub font-bold uppercase tracking-[0.18em] text-[#04A05C]/80 shrink-0" data-id="element-196" data-mp="vzr121sqg3rk">
        Live
      </span>
      <span className="h-3 w-px bg-[#04A05C]/40 shrink-0" data-id="element-197" data-mp="1520bkro03x4y" />
      <div className="relative flex-1 min-w-0 h-5" data-id="element-198" data-mp="151gtnaycp8qx">
        <AnimatePresence mode="wait" data-id="element-199" data-mp="31uin5kudky2n">
          <motion.div key={events[idx].id} initial={{
          opacity: 0,
          y: 6
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.4,
          ease: 'easeOut'
        }} className="absolute inset-0 flex items-center justify-center text-[11px] text-[#04A05C]/80 dark:text-[#34d399]/90 truncate whitespace-nowrap" data-id="element-200" data-mp="31vk8ci53v0y3">
            {events[idx].text}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>;
}
// =============================================================================
// Stat definition
// =============================================================================
type Stat = {
  key: string;
  icon: ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  value: React.ReactNode;
  suffix?: string;
  label: string;
  /** Tiny "hover detail" line that appears on hover/focus. */
  detail: React.ReactNode;
  /** Tiny sparkline data — visual energy under the value. */
  spark: number[];
  /** Where this stat links to (clickable for navigation context). */
  href?: string;
  /** Show pulsing "live" dot on the icon. */
  live?: boolean;
};
// =============================================================================
// Component
// =============================================================================
export function ByTheNumbers() {
  const yards = useLiveYards();
  const yardsToday = useYardsToday();
  const events = useLiveEvents(yardsToday);
  const [hovered, setHovered] = useState<string | null>(null);
  // Track the site theme so the decorative sparkline stroke (an SVG color
  // prop, not a class) can flip with light/dark. In LIGHT mode the bar is
  // inverted — green background with dark-green (#034E35) foreground — so the
  // sparkline uses the dark green; DARK mode keeps the original brand green.
  const [isDark, setIsDark] = useState(() => typeof document !== 'undefined' && document.documentElement.classList.contains('dark'));
  useEffect(() => {
    if (typeof document === 'undefined') return;
    const el = document.documentElement;
    const sync = () => setIsDark(el.classList.contains('dark'));
    sync();
    const obs = new MutationObserver(sync);
    obs.observe(el, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => obs.disconnect();
  }, []);
  const sparkColor = isDark ? '#04A05C' : '#4fbf99';
  const stats: Stat[] = [{
    key: 'yards',
    icon: Truck,
    value: <AnimatedNumber value={yards} data-id="element-201" data-mp="w18jyy48vyk3" />,
    suffix: '+',
    label: 'Yards Delivered',
    detail: <>
          <TrendingUp className="w-3 h-3 inline -mt-0.5 mr-1 text-[#04A05C] dark:text-[#04A05C]" data-id="element-202" data-mp="h8f61h4mfqhj" />
          +{yardsToday.toLocaleString()} today
        </>,
    spark: [3, 4, 5, 5, 7, 8, 9, 11, 12, 14, 15, 18],
    href: '/delivery-zones',
    live: true
  }, {
    key: 'sameday',
    icon: Zap,
    value: <AnimatedNumber value={95} data-id="element-203" data-mp="d6bt0o2b0du5" />,
    suffix: '%',
    label: 'Same-Day Delivery',
    detail: <>vs ~60% PNW industry avg</>,
    spark: [78, 82, 84, 87, 89, 90, 92, 93, 94, 94, 95, 95],
    href: '/#how-it-works'
  }, {
    key: 'reviews',
    icon: Star,
    value: <AnimatedNumber value={250} data-id="element-204" data-mp="2hn8au61jqmx0" />,
    suffix: '+',
    label: '5-Star Reviews',
    detail: <>4.9 ★ avg · Google + Yelp</>,
    spark: [12, 28, 45, 68, 92, 115, 138, 165, 190, 210, 232, 250],
    href: '/#reviews'
  }, {
    key: 'zips',
    icon: MapPin,
    value: <AnimatedNumber value={87} data-id="element-205" data-mp="2lnif9e9ox59r" />,
    label: 'ZIP Codes Served',
    detail: <>Pierce + King + South Snohomish</>,
    spark: [42, 48, 55, 60, 64, 68, 72, 76, 80, 83, 85, 87],
    href: '/delivery-zones'
  }, {
    key: 'years',
    icon: Calendar,
    value: <AnimatedNumber value={4} data-id="element-206" data-mp="2vdlehw7enfgj" />,
    label: 'Years in Business',
    detail: <>Family-owned · Locally rooted</>,
    spark: [1, 1, 2, 2, 3, 3, 3, 4, 4, 4, 4, 4],
    href: '/about'
  }];
  return <section aria-label="By the numbers" className="w-full relative overflow-hidden transition-colors duration-300 bg-[#f8eee3] dark:bg-[#000000]" data-id="element-207" data-mp="35cstsoxp9k6a">
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-6" data-id="element-208" data-mp="1ayi5chl0os11">
        {/* Eyebrow + live ticker */}

        {/* Stats row — horizontal scroll on mobile, even grid on md+ */}
        <ul className="flex md:grid md:grid-cols-5 gap-0 overflow-x-auto md:overflow-visible
                     -mx-4 sm:-mx-6 md:mx-0 px-4 sm:px-6 md:px-0
                     snap-x snap-mandatory md:snap-none scrollbar-hide" data-id="element-209" data-mp="1hdqqp60u363e">
          {stats.map((stat, idx) => {
          const Icon = stat.icon;
          const isHovered = hovered === stat.key;
          const Inner = <motion.div initial={{
            opacity: 0,
            y: 8
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            margin: '-40px'
          }} transition={{
            duration: 0.4,
            delay: idx * 0.06,
            ease: 'easeOut'
          }} whileHover={{
            y: -2
          }} className={`group relative h-full shrink-0 md:shrink min-w-[170px] md:min-w-0 snap-start
                            flex items-center gap-3 px-4 md:px-5 py-2 rounded-lg
                            transition-colors duration-200
                            ${stat.href ? 'cursor-pointer hover:bg-[#675A4E]/10 dark:hover:bg-[#04A05C]/12' : ''}`} onMouseEnter={() => setHovered(stat.key)} onMouseLeave={() => setHovered(null)} onFocus={() => setHovered(stat.key)} onBlur={() => setHovered(null)} data-id="element-210" data-mp="tmur3mq4z6ll">
                {/* Icon + live pulse */}
                <div className="relative shrink-0" data-id="element-211" data-mp="3knalbdfntuzb">
                  <motion.div animate={{
                scale: isHovered ? 1.1 : 1
              }} transition={{
                duration: 0.25
              }} data-id="element-212" data-mp="3ko3l1ug7bk7y">
                    <Icon className="w-5 h-5 text-[#4fbf99]/80 dark:text-[#655f54]/80 transition-colors group-hover:text-[#4fbf99] dark:group-hover:text-[#B88F43]" strokeWidth={1.5} data-id="element-213" data-mp="3ay0q3opirw6z" />
                  </motion.div>
                  {stat.live && <span className="absolute -top-0.5 -right-0.5 flex h-2 w-2" data-id="element-214" data-mp="31vhpzcd4bxof">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#034E35] dark:bg-[#716C65] opacity-75" data-id="element-215" data-mp="2wyix0x56xwrt" />
                      <span className="relative inline-flex rounded-full h-2 w-2 bg-[#4fbf99] dark:bg-[#655f54]" data-id="element-216" data-mp="2rb7avz26osbf" />
                    </span>}
                </div>

                <div className="min-w-0 flex-1" data-id="element-217" data-mp="2dcz99du7d2tx">
                  {/* Number + sparkline */}
                  <div className="flex items-baseline justify-between gap-2" data-id="element-218" data-mp="2ib11opdiqei2">
                    <p className="text-xl md:text-2xl font-extrabold leading-none tabular-nums font-heading text-[#1FB684] dark:text-[#716C65]" data-id="element-219" data-mp="3614t7ydfmnsp">
                      {stat.value}
                      {stat.suffix && <span className="text-base md:text-lg align-baseline text-[#1FB684]/80 dark:text-[#716C65]/80" data-id="element-220" data-mp="2wb1z1uukr3ss">
                          {stat.suffix}
                        </span>}
                    </p>
                    <Sparkline points={stat.spark} color={sparkColor} className="w-12 h-3 opacity-50 group-hover:opacity-100 transition-opacity" data-id="element-221" data-mp="3oy605oqjqxza" />
                  </div>

                  {/* Label OR detail — swap on hover. Same row height so
                   nothing else shifts. */}
                  <div className="relative h-3.5 mt-0.5 overflow-hidden" data-id="element-222" data-mp="1dajbnulpl59t">
                    <AnimatePresence mode="wait" initial={false} data-id="element-223" data-mp="1r2r1t3ihlxzj">
                      <motion.p key={isHovered ? 'detail' : 'label'} initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -6
                  }} transition={{
                    duration: 0.2,
                    ease: 'easeOut'
                  }} className={cn(`absolute inset-x-0 text-[10px] font-bold uppercase tracking-[0.12em] leading-tight whitespace-nowrap
                                    ${isHovered ? 'normal-case tracking-normal' : ''}`, 'text-[#4fbf99] dark:text-[#716C65]', 'text-[#1FB684]')} data-id="element-224" data-mp="1r53h18wv1u8w">
                        {isHovered ? stat.detail : stat.label}
                      </motion.p>
                    </AnimatePresence>
                  </div>
                </div>
              </motion.div>;
          return <li key={stat.key} className="md:contents" data-id="element-225" data-mp="33repho9e3qsz" data-mpi={idx}>
                {stat.href ? stat.href.startsWith('/#') ? <a href={stat.href} aria-label={`${stat.label} — learn more`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22c95c]/50 rounded-lg" data-id="element-226" data-mp="37tjlgtnb8zis">
                      {Inner}
                    </a> : <Link to={stat.href} aria-label={`${stat.label} — learn more`} className="block focus:outline-none focus-visible:ring-2 focus-visible:ring-[#22c95c]/50 rounded-lg" data-id="element-227" data-mp="3mjr3x9dbiijh">
                      {Inner}
                    </Link> : Inner}
              </li>;
        })}
        </ul>
      </div>
    </section>;
}